import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { pickListsApi, type ListPickListsParams } from '@/api/pickListsApi'
import type {
  CreateZonePayload,
  GeneratePickListPayload,
  UpdatePickListPayload,
  UpdateZonePayload,
} from '@/types/pickList'
import { useTenant } from '@/providers/TenantProvider'

const PICK_LISTS = 'pick-lists'
const ZONES = 'zones'

// ─── Queries ────────────────────────────────────────────────────────────────

export function usePickListsList(params: ListPickListsParams = {}) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: ['inventory', PICK_LISTS, tenantId, params],
    enabled: !!tenantId,
    queryFn: () => pickListsApi.list(tenantId as string, params),
  })
}

export function usePickList(id: string | undefined) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: ['inventory', PICK_LISTS, 'detail', tenantId, id],
    enabled: !!tenantId && !!id,
    queryFn: () => pickListsApi.get(tenantId as string, id as string),
  })
}

/**
 * The people a sheet may be assigned to: this tenant's enabled members.
 *
 * `enabled` must be the caller's `pick_lists.generate` check. The route is gated
 * on it, and a 403 here would read as an empty roster rather than as a refusal.
 */
export function usePickListAssignableUsers(enabled: boolean) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: ['inventory', PICK_LISTS, 'assignable-users', tenantId],
    enabled: enabled && !!tenantId,
    queryFn: () => pickListsApi.listAssignableUsers(tenantId as string),
  })
}

export function useStorageZones(params: { active?: boolean } = {}) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: ['inventory', ZONES, tenantId, params],
    enabled: !!tenantId,
    queryFn: () => pickListsApi.listZones(tenantId as string, params),
  })
}

/**
 * The templates a kit run can be started from.
 *
 * `/inventory/kits/templates` rather than filtering the general procedure-
 * template list by type: it returns every KIT-typed template WITH `runnable`
 * and `blockedReason`, so a template that cannot be run can be shown disabled
 * and explained instead of silently vanishing from the picker.
 */
export function useKitTemplates(enabled = true) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: ['inventory', 'kits', 'templates', tenantId],
    enabled: !!tenantId && enabled,
    queryFn: () => pickListsApi.listKitTemplates(tenantId as string),
  })
}

/** Start a kit run from a kit template. Invalidates every kit list. */
export function useCreateKitProcedure() {
  const { selectedTenantId: tenantId } = useTenant()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: {
      procedureTemplateId: string
      assigneeId?: string
      quantity?: number
      scheduledDate?: string
      workOrderNumber?: string
      customerId?: string
    }) =>
      pickListsApi.createKitProcedure(
        tenantId as string,
        vars.procedureTemplateId,
        vars.assigneeId,
        vars.quantity,
        vars.scheduledDate,
        vars.workOrderNumber,
        vars.customerId,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['inventory', 'kits'] })
    },
  })
}

/** `enabled` so a screen that only sometimes shows kits can skip the fetch. */
export function useKitForms(enabled = true) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: ['inventory', 'kits', 'forms', tenantId],
    enabled: !!tenantId && enabled,
    queryFn: () => pickListsApi.listKitForms(tenantId as string),
  })
}

export function useKitInventory(enabled = true) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: ['inventory', 'kits', 'inventory', tenantId],
    enabled: !!tenantId && enabled,
    queryFn: () => pickListsApi.listKitInventory(tenantId as string),
  })
}

// ─── Mutations ──────────────────────────────────────────────────────────────

/**
 * Resolve a sheet without saving it.
 *
 * A mutation rather than a query because it is a POST with a body and is fired
 * on demand from a menu — there is nothing to cache and re-running it should
 * always hit the server for current stock.
 */
export function usePreviewPickList() {
  const { selectedTenantId: tenantId } = useTenant()
  return useMutation({
    mutationFn: (payload: GeneratePickListPayload) =>
      pickListsApi.preview(tenantId as string, payload),
  })
}

export function useGeneratePickList() {
  const { selectedTenantId: tenantId } = useTenant()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: GeneratePickListPayload) =>
      pickListsApi.generate(tenantId as string, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['inventory', PICK_LISTS] })
    },
  })
}

export function useUpdatePickList(id: string) {
  const { selectedTenantId: tenantId } = useTenant()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: UpdatePickListPayload) =>
      pickListsApi.update(tenantId as string, id, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['inventory', PICK_LISTS] })
    },
  })
}

/**
 * Record the quantity picked for one line.
 *
 * The server answers with the whole sheet, so the detail query is seeded from
 * the response rather than refetched — the row's status and the header's
 * progress must move in the same paint, and a refetch would flash the old
 * numbers first. The list query is still invalidated, since a sheet moving
 * OPEN → IN_PROGRESS changes what the index shows, and so are the process
 * steps the pick was written into (z8nrz7fjzc).
 */
export function useUpdatePickListLine(id: string) {
  const { selectedTenantId: tenantId } = useTenant()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ lineId, qtyPicked }: { lineId: string; qtyPicked: number | null }) =>
      pickListsApi.updateLine(tenantId as string, id, lineId, qtyPicked),
    onSuccess: (pickList) => {
      // The SAME key `usePickList` reads — a near-miss here fails silently,
      // leaving the cell to look saved while the row's status never moves.
      qc.setQueryData(['inventory', PICK_LISTS, 'detail', tenantId, id], pickList)
      void qc.invalidateQueries({ queryKey: ['inventory', PICK_LISTS] })
      // The pick was written into the steps it feeds (z8nrz7fjzc): their
      // selections and holds, wherever a form shows them.
      void qc.invalidateQueries({ queryKey: ['process-steps', tenantId] })
    },
  })
}

export function useRevisePickList(id: string) {
  const { selectedTenantId: tenantId } = useTenant()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => pickListsApi.revise(tenantId as string, id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['inventory', PICK_LISTS] })
    },
  })
}

export function useDeletePickList() {
  const { selectedTenantId: tenantId } = useTenant()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => pickListsApi.remove(tenantId as string, id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['inventory', PICK_LISTS] })
    },
  })
}

/**
 * Record a print for the audit trail.
 *
 * Intentionally invalidates nothing: printing changes no list state, and
 * invalidating would refetch the sheet the user is currently reading.
 */
export function useRecordPickListPrint() {
  const { selectedTenantId: tenantId } = useTenant()
  return useMutation({
    mutationFn: (id: string) => pickListsApi.recordPrint(tenantId as string, id),
  })
}

// ─── Kit assembly ───────────────────────────────────────────────────────────

const KITS = 'kits'

/**
 * Kit mutations return the refreshed build, so the detail query is seeded from
 * the response rather than refetched.
 *
 * Only cancel remains. Assembling a kit is initiating, ending and completing
 * the kit form's process step — scan/sign/complete were a second lifecycle over
 * the same record and are gone.
 */
function useKitMutation<TVars>(
  fn: (tenantId: string, id: string, vars: TVars) => Promise<import('@/types/pickList').KitBuild>,
  id: string,
) {
  const { selectedTenantId: tenantId } = useTenant()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: TVars) => fn(tenantId as string, id, vars),
    onSuccess: (build) => {
      qc.setQueryData(['inventory', KITS, 'build', tenantId, id], build)
      void qc.invalidateQueries({ queryKey: ['inventory', KITS, 'forms'] })
      void qc.invalidateQueries({ queryKey: ['inventory', KITS, 'inventory'] })
    },
  })
}

/** One finished kit's record. Cached normally — a completed kit does not change. */
export function useKitDetail(buildId: string | undefined) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: ['inventory', KITS, 'detail', tenantId, buildId],
    enabled: !!tenantId && !!buildId,
    queryFn: () => pickListsApi.getKitDetail(tenantId as string, buildId as string),
  })
}

export function useCancelKitBuild(id: string) {
  return useKitMutation<string | undefined>((t, i, reason) => pickListsApi.cancelKitBuild(t, i, reason), id)
}

// ─── Zone mutations ─────────────────────────────────────────────────────────

export function useSaveZone() {
  const { selectedTenantId: tenantId } = useTenant()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { id?: string; payload: CreateZonePayload | UpdateZonePayload }) =>
      vars.id
        ? pickListsApi.updateZone(tenantId as string, vars.id, vars.payload)
        : pickListsApi.createZone(tenantId as string, vars.payload as CreateZonePayload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['inventory', ZONES] })
      // A zone rename changes what future sheets print, so drop cached lists.
      void qc.invalidateQueries({ queryKey: ['inventory', PICK_LISTS] })
    },
  })
}

export function useDeleteZone() {
  const { selectedTenantId: tenantId } = useTenant()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => pickListsApi.deleteZone(tenantId as string, id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['inventory', ZONES] })
      void qc.invalidateQueries({ queryKey: ['inventory', 'materials'] })
    },
  })
}

export function useSeedDefaultZones() {
  const { selectedTenantId: tenantId } = useTenant()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => pickListsApi.seedDefaultZones(tenantId as string),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['inventory', ZONES] })
    },
  })
}
